import React from 'react';
import { Compass, ChevronRight, Volume2, ArrowRight, ArrowLeft, Package, FileText } from 'lucide-react';
import { ArchivalImageCard } from '../ArchivalImageCard';
import { ArchiveTab } from '../../types';
import { useVoiceReader } from '../../context/VoiceReaderContext';

interface SectionProps {
  onNavigate?: (tab: ArchiveTab) => void;
}

export const EssentialsForNewLandSection: React.FC<SectionProps> = ({ onNavigate }) => {
  const { speakCurrentPage, isPlaying } = useVoiceReader();

  return (
    <div className="max-w-4xl mx-auto space-y-10 pb-16 font-sans text-[#2c221e]">
      
      {/* Header & Breadcrumbs & Voice Reader */}
      <header className="bg-[#f3ede0] p-6 sm:p-8 md:p-12 rounded-3xl border-2 border-[#d8ceb9] shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-serif text-[#7a5b3a]">
          {/* Breadcrumb Navigation */}
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            {onNavigate && (
              <button
                onClick={() => onNavigate('journey')}
                className="hover:underline flex items-center gap-1 font-bold text-[#7a5b3a]"
              >
                <Compass className="w-3.5 h-3.5" />
                <span>The Journey</span>
              </button>
            )}
            <ChevronRight className="w-3.5 h-3.5 text-[#d8ceb9]" />
            <span className="bg-[#e8dec9] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider text-[#7a5b3a] text-[11px] sm:text-xs">
              Essentials for Starting a New Life in NZ
            </span>
          </nav>

          {/* Audio Narration Trigger */}
          <button
            onClick={speakCurrentPage}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white text-xs font-bold transition shadow-xs"
            title="Listen to Essentials for Starting a New Life in NZ"
          >
            <Volume2 className={`w-3.5 h-3.5 ${isPlaying ? 'animate-pulse text-amber-200' : 'text-amber-300'}`} />
            <span>{isPlaying ? 'Listening to Section...' : 'Listen to this Chapter'}</span>
          </button>
        </div>

        <h1 className="font-serif text-3xl md:text-5xl font-extrabold text-[#2c221e] tracking-tight leading-tight pt-1">
          WHAT A WOMAN WOULD PACK WHEN EMIGRATING TO A NEW COUNTRY IN 1850
        </h1>

        <div className="flex items-center gap-2 text-xs font-serif text-[#7a5b3a] uppercase tracking-wider font-semibold">
          <Package className="w-3.5 h-3.5" />
          <span>Provisions, Wardrobes, Tools, Machinery & Arrival in 1850</span>
        </div>
      </header>

      {/* Main Archival Text Content */}
      <article className="bg-[#fbf9f4] p-6 sm:p-10 md:p-12 rounded-3xl border border-[#d8ceb9] shadow-sm space-y-8 text-base md:text-lg leading-relaxed text-[#2c221e]">
        
        {/* Section 1: What a Woman Would Pack */}
        <div className="space-y-4">
          <p>
            Imagine packing up to leave your home forever, knowing it would be difficult to get new clothes in the new land, and not really having any idea of the climate or services to clean and maintain your wardrobe.
          </p>

          {/* Image 9: Packing case */}
          <ArchivalImageCard
            src="/images/9 packing case.jpg"
            alt="Packing case"
            caption="Packing case for 1850 emigration voyage"
            filenameHint="9 packing case.jpg"
          />

          <p>
            Anne Denton had to supervise the three indoor servants to prepare and pack for herself, Frederick and their seven children. She would have taken advice from the New Zealand Company and the Canterbury Association. There were several helpful books such as the <em>Hand-book for Intending Emigrant to the Southern Settlements of New Zealand, 1849</em> which made these suggestions for ladies leaving their home country.
          </p>

          {/* Women's wardrobe list */}
          <div className="bg-[#f3ede0]/80 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-2">
            <ul className="list-disc list-outside pl-6 space-y-1.5 text-[#2c221e]">
              <li>48 calico or cambric chemises</li>
              <li>2 coloured dressing gowns</li>
              <li>8 white muslin do.</li>
              <li>4 coloured morning dresses – examples below</li>
            </ul>
          </div>

          {/* Images 10 & 11: Pink & Brown dresses */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <ArchivalImageCard
              src="/images/10 Pink dress.jpg"
              alt="Pink dress"
              caption="Pink morning dress, mid-19th century"
              filenameHint="10 Pink dress.jpg"
            />
            <ArchivalImageCard
              src="/images/11 brown dress.jpg"
              alt="Brown dress"
              caption="Brown morning dress, mid-19th century"
              filenameHint="11 brown dress.jpg"
            />
          </div>

          {/* Women's wardrobe list continued */}
          <div className="bg-[#f3ede0]/80 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-2">
            <ul className="list-disc list-outside pl-6 space-y-1.5 text-[#2c221e]">
              <li>8 muslin dresses, 4 dinner dresses and 3 pairs of stays</li>
            </ul>
          </div>

          {/* Images 12 & 13: Green & Cream dinner dresses */}
          <div className="space-y-2 my-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ArchivalImageCard
                src="/images/12 green dress.jpg"
                alt="Green dress"
                caption="Green dinner dress"
                filenameHint="12 green dress.jpg"
              />
              <ArchivalImageCard
                src="/images/13 cream dress.jpg"
                alt="Cream dress"
                caption="Cream dinner dress"
                filenameHint="13 cream dress.jpg"
              />
            </div>
            <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
              Dinner dresses
            </p>
          </div>

          {/* Image 14: Ladies boots */}
          <div className="my-6">
            <ArchivalImageCard
              src="/images/14 ladies boots.jpg"
              alt="Ladies boots"
              caption="Ladies leather boots of the Victorian colonial era"
              filenameHint="14 ladies boots.jpg"
            />
          </div>

          {/* Women's wardrobe list continued */}
          <div className="bg-[#f3ede0]/80 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-2">
            <ul className="list-disc list-outside pl-6 space-y-1.5 text-[#2c221e]">
              <li>8 pairs of shoes and 2 bonnets</li>
            </ul>
          </div>

          {/* Image 15: Vanity box */}
          <div className="my-6">
            <ArchivalImageCard
              src="/images/15 Vanity box.jpg"
              alt="Vanity box"
              caption="Travelling vanity box / dressing case"
              filenameHint="15 Vanity box.jpg"
            />
          </div>

          <p>
            Women arrived at the tiny settlement at Port Lyttelton, undertook a strenuous walk over the Port Hills in summer heat, and to a camp called Pilgrim Spring in Hagley Park. They must have looked at the dinner dresses and wondered where they would wear them. The Dentons moved from Hagley Park to a camp in what is now Cathedral Square. The surviving family later moved to their land at Opawa, living in a cavity dug into the side of the steep bank with a canvas sheet over the opening until they could build a house.
          </p>

          <p>
            Alice Denton later said that her mother was most upset there were no servants or nurses to look after the children, there was no house to live in, and it drove her to tears that there were no shops.
          </p>

          <p>
            They made makeshift tents using canvas covers made for their transported farm machinery. For all the prior planning for the new town, not enough consideration had been given to the need for proper sanitation in a crowded and unhygienic environment, and “fever”, typhoid, broke out and was caught by the Dentons.
          </p>

          <p>
            Early on Anne would have looked at things like the recipes for wine that she had brought out – and put them aside with a deep sigh. She had lamented the lack of shops and facilities, and it must have been a drastic change for a woman used to having servants do the household work.
          </p>

          <p>
            The sad bells would have tolled four times for the Dentons in 1851 – in the space of 97 days (2 days shorter than the voyage out by ship) they were for Georgiana on 8 May, then for William on 22 June, for Edward on 25 June, and then for their mother on 13 August (although there is some confusion over the actual date).
          </p>

          <p>
            It took the surviving sons 10 years to pay Dr Barker’s medical bills for them and for John Frederick, who died in 1856.
          </p>
        </div>

        <hr className="border-t-2 border-dashed border-[#d8ceb9] my-8" />

        {/* Section 2: What Did the Men Require? */}
        <div className="space-y-6">
          <div className="border-b-2 border-[#d8ceb9] pb-2">
            <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-[#2c221e] tracking-tight">
              WHAT DID THE MEN REQUIRE?
            </h2>
          </div>

          <p>
            The New Zealand Company recommended this list for the men.
          </p>

          <div className="bg-[#f3ede0]/80 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-2">
            <ul className="list-disc list-outside pl-6 space-y-1.5 text-[#2c221e]">
              <li>2 lined fustian jackets (made of heavy woven cotton) &amp; 2 pairs of fustian trousers</li>
              <li>2 pairs of duck trousers (high backed, buttoned fly, 2 front pockets, 1 rear pocket)</li>
              <li>12 cotton shirts &amp; 6 handkerchiefs</li>
              <li>2 round frocks</li>
            </ul>
          </div>

          {/* Image 16: Man's formal wear */}
          <div className="my-6">
            <ArchivalImageCard
              src="/images/16 man_s formal wear.jpg"
              alt="Man's formal wear"
              caption="Man's formal wear of the 1850 period"
              filenameHint="16 man_s formal wear.jpg"
            />
          </div>

          <div className="bg-[#f3ede0]/80 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-2">
            <ul className="list-disc list-outside pl-6 space-y-1.5 text-[#2c221e]">
              <li>6 pairs worsted stockings</li>
              <li>2 Scotch caps</li>
              <li>1 pair hobnail boots and 1 pair of shoes</li>
            </ul>
          </div>
        </div>

        <hr className="border-t-2 border-dashed border-[#d8ceb9] my-8" />

        {/* Section 3: The School Plan That Never Happened */}
        <div className="space-y-4">
          <div className="border-b-2 border-[#d8ceb9] pb-2">
            <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-[#2c221e] tracking-tight">
              THE SCHOOL PLAN THAT NEVER HAPPENED ON SIR GEORGE SEYMOUR
            </h2>
          </div>

          <p>
            There were many children on the ships and their continuing education was considered. Each of the Four Ships had a schoolmaster on board but as the extract below shows<sup>1</sup>, somehow the time passed so pleasantly or the sea was so rough that the scheme for a school never happened. The two Denton referred to would have been Frederick (13) and Edward (11).
          </p>
        </div>

        <hr className="border-t-2 border-dashed border-[#d8ceb9] my-8" />

        {/* Section 4: The Dentons Came Out Prepared for Farming */}
        <div className="space-y-6">
          <div className="border-b-2 border-[#d8ceb9] pb-2">
            <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-[#2c221e] tracking-tight">
              THE DENTONS CAME OUT PREPARED FOR FARMING
            </h2>
          </div>

          <p>
            John Frederick Denton came well prepared for farming, but perhaps not for the conditions they would face – for example, there weren’t horses readily available, so they used bullocks first.
          </p>

          <p className="font-serif font-semibold text-[#4a3525]">
            He brought a farm cart.
          </p>

          {/* Image 21: Farm cart */}
          <ArchivalImageCard
            src="/images/21 farm cart.jpg"
            alt="Farm cart"
            caption="Farm cart brought to Canterbury in 1850"
            filenameHint="21 farm cart.jpg"
          />

          <p className="font-serif font-semibold text-[#4a3525]">
            He also brought the very useful and practical tip drays.
          </p>

          {/* Image 22: Tip dray */}
          <ArchivalImageCard
            src="/images/22 tip dray.jpg"
            alt="Tip dray"
            caption="Tip dray cart used for clearing land, moving earth and agricultural transport"
            filenameHint="22 tip dray.jpg"
          />

          <p>
            Ploughs were essential to break the ground ready for cultivation, and a source of income by ploughing for others. They could be pulled by horses or bullocks.
          </p>

          {/* Image 23: Plough */}
          <ArchivalImageCard
            src="/images/23 plough.jpg"
            alt="Plough"
            caption="Heavy iron swing plough brought to break Canterbury tussock"
            filenameHint="23 plough.jpg"
          />

          <p>
            Frederick brought out complete sets of harness for horse teams, although they had to wait for a supply of horses to put the gear on. Bullocks bred by the Deans brothers were used instead.
          </p>

          <p>
            They brought grain and pasture seeds including cocksfoot grass – it is believed it was Denton cocksfoot that was the first planted in NZ.
          </p>
        </div>

        <hr className="border-t-2 border-dashed border-[#d8ceb9] my-8" />

        {/* Section 5: Louisa Alice Denton's Toys */}
        <div className="space-y-6">
          <div className="border-b-2 border-[#d8ceb9] pb-2">
            <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-[#2c221e] tracking-tight">
              LOUISA ALICE DENTON’S TOYS
            </h2>
          </div>

          <p>
            Although the Dentons came prepared for a life of farming they also brought out a wooden doll’s wardrobe for their young daughter Louisa Alice (5).
          </p>

          {/* Images 24 & 25: Alice's doll's house & inside */}
          <div className="space-y-2 my-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ArchivalImageCard
                src="/images/24 Alice_s dolls house.jpg"
                alt="Alice's dolls house"
                caption="Alice's doll's house"
                filenameHint="24 Alice_s dolls house.jpg"
              />
              <ArchivalImageCard
                src="/images/25 inside dolls house.jpg"
                alt="Inside dolls house"
                caption="Inside the 1850 doll's house"
                filenameHint="25 inside dolls house.jpg"
              />
            </div>
            <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
              These are examples of an 1850 doll’s House – Photo: Denton Book<sup>2</sup>
            </p>
          </div>

          <p>
            Some people bought out pianos, there was a prefabricated cottage, and it took about six weeks for whaleboats to bring all the luggage ashore, from Lyttelton round to Sumner and over the dangerous bar into the estuary then up the Avon River to the centre of the planned city. The Dentons, as did most of the colonists, stayed on board the ship for the first weeks.
          </p>

          {/* Image 26: Prefabricated cottage */}
          <div className="space-y-2 my-6">
            <ArchivalImageCard
              src="/images/26 cottage from 1850.jpg"
              alt="Prefabricated cottage from 1850s"
              caption="Example of prefabricated cottage from 1850s"
              filenameHint="26 cottage from 1850.jpg"
            />
            <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
              Example of prefabricated cottage from 1850s
            </p>
          </div>

          <p>
            The first brush with bureaucracy came as soon as the colonist berthed. In England they had been assured they would not have to pay any customs or taxes on the goods they took with them but the local customs collector wanted money. Robert Godley appealed to Governor Grey and the customs collector waivered the duty.
          </p>

          {/* Historical Quote from Denton Book */}
          <blockquote className="my-6 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] text-[#3d2a1d] font-serif text-base sm:text-lg leading-relaxed space-y-2 shadow-xs">
            <p className="italic">
              “For the men and children it was like a prolonged camping holiday, but for the women it was unmitigated drudgery, preparing meals on open fires and trying to wash clothes in buckets. Lyttelton was very short of water….There was no shortage of food, as the local Maori from the nearby bay of Rapaki sold fish and potatoes to the settlers, while the Rhodes brothers, pioneer sheep farmers at Purau across the harbour, brought fresh met to Lyttelton by whaleboat nearly every day.”<sup>2</sup>
            </p>
          </blockquote>

          <p>
            There were about 300 people living in Lyttelton. They were expecting the ships but they weren’t expecting them to arrive almost together and that stretched their resources and accommodation beyond its limits.
          </p>

          <p>
            Lyttelton had been planned as the main settlement but it was soon obvious that wasn’t going to be suitable. The settlers wanted to get going, to get their land but that was not a fast process and some people squatted on land in the meantime. But as the ships had arrived in mid-December, thoughts turned to Christmas in what seemed to them to be a novelty of hot weather and blue skies. They had the church services and many had traditional Christmas fare, including Christmas puddings!
          </p>
        </div>

        {/* Footnotes Section */}
        <footer className="pt-8 mt-10 border-t-2 border-[#d8ceb9] text-sm text-[#5c4328] space-y-3 font-serif">
          <div className="flex items-center gap-2 font-bold uppercase tracking-wider text-xs text-[#7a5b3a]">
            <FileText className="w-4 h-4" />
            <span>Footnotes</span>
          </div>
          <ol className="list-decimal list-outside pl-5 space-y-1.5 text-xs sm:text-sm leading-relaxed">
            <li>From the Centennial Day Souvenir Programme, 16 December 1950</li>
            <li>Denton Book</li>
          </ol>
        </footer>

      </article>

      {/* Chapter Sub-Navigation Footer */}
      {onNavigate && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-6 bg-[#f3ede0] rounded-3xl border-2 border-[#d8ceb9]">
          <button
            onClick={() => onNavigate('journey-suffolk')}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#e8dec9] hover:bg-[#d8ceb9] text-[#2c221e] font-serif text-sm font-bold border border-[#d8ceb9] transition"
          >
            <ArrowLeft className="w-4 h-4 text-[#7a5b3a]" />
            <span>Previous: From Suffolk to the Southern Seas</span>
          </button>
          
          <button
            onClick={() => onNavigate('journey-why-they-came')}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white font-serif text-sm font-bold transition shadow-xs"
          >
            <span>Next: Why they came around the curve of the Earth</span>
            <ArrowRight className="w-4 h-4 text-amber-200" />
          </button>
        </div>
      )}

    </div>
  );
};
